import { Box, Button, Card, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useUpdateInvoice } from 'api';
import { Comments } from 'app/Private/Comments';
import CustomVariables from 'app/Private/CustomVariables/CustomVariables';
import CustomVariablesForm from 'app/Private/CustomVariables/CustomVariablesForm/CustomVariablesForm';
import InvoiceDetailTitle from 'app/Private/Invoices/Invoice/InvoiceDetailTitle';
import classNames from 'classnames/bind';
import { SuperButton } from 'components/SuperButton';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { isNonNullish, isNullish } from 'remeda';
import Conversation from 'shared/components/ActivityDetail/Conversation';
import DebtorInfo from 'shared/components/DebtorInfo';
import { Icon, IconName } from 'shared/components/Icon';
import UnmatchPayment from 'shared/components/InvoiceActionsDropdown/UnmatchPayment';
import UnmatchCreditNote from 'shared/components/InvoiceActionsDropdown/UnmatchPayment/UnmatchCreditNote';
import StatusItem from 'shared/components/StatusItem';
import { useCreateInvoiceComment } from 'shared/hooks/use-create-invoice-comment';
import { useLoadComments } from 'shared/hooks/use-load-comments';
import { useRevertFormalNotice } from 'shared/hooks/use-revert-formal-notice';
import { isDraft, isPaid } from 'shared/utils/invoice-status';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { InvoiceFeedback } from 'types/store/activity';
import type { CreditNote } from 'types/store/credit-note-state';
import type { NextStepInformation, Invoice as TInvoice } from 'types/store/invoice-state';
import type { StoreState } from 'types/storeTypes';
import InvoiceDebtorSituation from './InvoiceDebtorSituation/InvoiceDebtorSituation';
import InvoiceDetailContext from './InvoiceDetail.context';
import InvoiceInfo from './InvoiceInfo';
import InvoiceRecoveryPlan from './InvoiceRecoveryPlan';
import InvoiceTimeline from './InvoiceTimeline/InvoiceTimeline';
import { LatePaymentInfo } from './LatePaymentInfo';
import ProductsDetail from './ProductsDetail';
import styleIdentifiers from './ToHandleDetail.module.scss';

const styles = classNames.bind(styleIdentifiers);

const nextStepTitle = {
  Lawyer: 'THIRD_PARTY_CASES.CASE_TYPE.LAWYER',
  Bailiff: 'THIRD_PARTY_CASES.CASE_TYPE.BAILIFF',
  'Debt collector': 'THIRD_PARTY_CASES.CASE_TYPE.DEBT_COLLECTOR', // I know. -Leo.
  common: 'INVOICE.NEXT_STEP',
};

type Props = {
  // todo: refactor: the parent should not be responsible for providing the type of the entity
  isCreditNote?: boolean;
  id?: number | string;
  modal?: boolean;
};

export default function Invoice({ isCreditNote = false, id, modal }: Props) {
  const history = useHistory();
  const { t } = useTranslation();
  const { params } = useRouteMatch<{ id: string; activity_id: string }>();
  const [
    isCustomeVariablesModalOpened,
    { open: openCustomVariablesModal, close: closeCustomVariablesModal },
  ] = useDisclosure(false);

  const creditNote = useSelector((state: StoreState) => state.creditNote.current?.data);
  const invoice = useSelector((state: StoreState) => state.invoice.current?.data);
  const activities = useSelector(
    (state: StoreState) => state[isCreditNote ? 'creditNote' : 'invoice'].currentActivity?.data,
  );
  const activitiesMetadata = useSelector(
    (state: StoreState) => state[isCreditNote ? 'creditNote' : 'invoice'].currentActivity?.metadata,
  );

  const data = isCreditNote ? creditNote : invoice;
  const { mutate: updateInvoice } = useUpdateInvoice();
  const { createInvoiceComment, isCreateInvoiceCommentLoading } = useCreateInvoiceComment();
  const { comments } = useLoadComments(
    { resourceType: 'invoice', resourceId: data?.id as number },
    isNonNullish(data?.id),
  );
  const { revertFormalNotice } = useRevertFormalNotice();

  useEffect(() => {
    const idResource = id ?? params.id;
    const action = isCreditNote ? creditNoteActions : invoiceActions;

    action.info({
      id: idResource,
      callback: ({ data: _data }: any) => {
        if (isDraft(_data.attributes)) {
          if (modal) dialogHide(DialogShowId.INVOICE);
          history.push(`/${isCreditNote ? 'credit-notes' : 'invoices'}/draft/${idResource}`);
        }
      },
    });
  }, [id, params.id]);

  // This flag is passed by the back when generating the URL, it should instead send the user to the TP case directly.
  useEffect(() => {
    const queryString = qs.parse(location.search);
    if (queryString.show_case && data && activities) {
      history.push(location.pathname);
      // eslint-disable-next-line
      showConversation(data)();
    }
  }, [activities, data]);

  const clientData = data?.debtor_attributes;

  const reloadInfo = () => {
    if (isNullish(data)) return;
    if (isCreditNote) creditNoteActions.info({ id: data.id, noReset: true, noLoading: true });
    else invoiceActions.info({ id: data.id, noReset: true, noLoading: true });
  };

  const unmatchPayment = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.LARGE,
      title: getModalTitle(),
      children: isCreditNote ? (
        <UnmatchCreditNote reload={reloadInfo} creditNote={data as CreditNote} />
      ) : (
        <UnmatchPayment reload={reloadInfo} invoice={data as TInvoice} />
      ),
    });
  };

  const invoiceContext = useMemo(
    () => ({
      data: data as TInvoice,
      isCreditNote,
      unmatchPayment,
      clientData,
      reloadInfo,
    }),
    [data, isCreditNote, unmatchPayment, clientData, reloadInfo],
  );

  if (!data) {
    return (
      <div
        style={{ height: '100vh' }}
        className={styles('ToHandleDetail', data && modal && 'modal')}
      >
        <LoadingOverlay visible />
      </div>
    );
  }

  const getMoreActivities = () => {
    invoiceActions.getActivityPage({
      id: data.id,
      noLoading: true,
      data: {
        page: activitiesMetadata.pagination.current_page + 1,
      },
    });
  };

  const onSendMessage = () => reloadInfo();

  const showConversation = (currentInvoice: TInvoice) => () => {
    if (isNullish(activities)) return;

    const invoiceFeedback = activities?.find(
      activity => activity.invoice_feedback,
    )?.invoice_feedback;

    const title = (currentInvoice as any).next_step_information.type;

    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(nextStepTitle[title as keyof typeof nextStepTitle] ?? nextStepTitle.common),
      children: (
        <div style={{ padding: 20 }}>
          {invoiceFeedback != null ? (
            <Conversation
              invoiceId={data.id}
              onSubmit={onSendMessage}
              caseId={data.third_party_case_id}
              invoiceFeedback={invoiceFeedback!}
            />
          ) : (
            <Conversation
              invoiceId={data.id}
              onSubmit={onSendMessage}
              caseId={data.third_party_case_id}
              invoiceFeedback={
                {
                  conversation: (currentInvoice.next_step_information as NextStepInformation)
                    .conversation!,
                  id: (currentInvoice.next_step_information as NextStepInformation)
                    .invoice_feedback_id,
                } as InvoiceFeedback
              }
            />
          )}
        </div>
      ),
    });
  };

  const getModalTitle = (): string => {
    const { external_payments, manual_payments, credit_notes_attributes } = data;

    if (isCreditNote) return t(i18nKeys.PAYMENT);

    if (external_payments?.length) return t(i18nKeys.INVOICE.UNMATCH_PAYMENT.RECONCILED_PAYMENTS);
    if (manual_payments?.length) return t(i18nKeys.INVOICE.UNMATCH_PAYMENT.PAYMENTS_ADDED_MANUALLY);
    if (credit_notes_attributes?.length) return t(i18nKeys.INVOICE.UNMATCH_PAYMENT.CREDIT_NOTES);

    return t(i18nKeys.PAYMENT);
  };

  const editInvoice = (values: any) => {
    if (isCreditNote) {
      creditNoteActions.update({
        data: {
          id: data.id,
          ...values,
        },
        callback: () => {
          (reloadInfo as any)();
        },
      });
    } else {
      updateInvoice(
        { id: data.id, data: values },
        {
          onSuccess: () => {
            closeCustomVariablesModal();
            (reloadInfo as any)();
          },
        },
      );
    }
  };

  const revertFormalNoticeStatus = () => {
    revertFormalNotice(
      { id: data.id.toString() },
      {
        onSuccess: () => {
          reloadInfo();
        },
      },
    );
  };

  return (
    <>
      <InvoiceDetailContext.Provider value={invoiceContext}>
        <div className={styles('ToHandleDetail', data && modal && 'modal')}>
          {data && !isDraft(data) && (
            <div className={styles('invoice-wrapper')}>
              <InvoiceDetailTitle />
              <Card className={styles('information-card')}>
                {isCreditNote || data.step_collection === null ? (
                  <div className={styles('check')}>
                    {data.status} {isPaid(data) && <Icon name={IconName.CHECK} />}
                    {!isCreditNote &&
                      (data.next_step_information as NextStepInformation)?.conversation && (
                        <Button c="white" size="compact-sm" onClick={showConversation(data)}>
                          {t(i18nKeys.SHOW_CONVERSATION)}
                        </Button>
                      )}
                  </div>
                ) : (
                  <>
                    <StatusItem step={data.step_collection} isLate />
                    <span>
                      {data.status}
                      {(data.next_step_information as NextStepInformation)?.conversation && (
                        <Button c="white" size="compact-sm" onClick={showConversation(data)}>
                          {t(i18nKeys.SHOW_CONVERSATION)}
                        </Button>
                      )}
                    </span>
                    {data.step_collection === 3 && (
                      <SuperButton onClick={revertFormalNoticeStatus} style={{ color: 'white' }}>
                        Revert formal notice
                      </SuperButton>
                    )}
                  </>
                )}
              </Card>
              <div className={styles('line-wrapper')}>
                <InvoiceInfo modal={modal} />
                <LatePaymentInfo />
                {clientData && (
                  <>
                    <DebtorInfo clientData={clientData} invoiceData={data} />
                    <InvoiceDebtorSituation />
                  </>
                )}
              </div>
              {Object.keys(data.custom_variables).length > 0 && (
                <CustomVariables
                  data={data.custom_variables}
                  handleSettings={openCustomVariablesModal}
                />
              )}
              <Space h="xs" />
              <InvoiceTimeline
                nextAction={
                  !isPaid(data) && (data.next_step_information as NextStepInformation)?.description
                }
                metadata={activitiesMetadata}
                activities={activities}
                invoiceId={data.id}
                seeMore={getMoreActivities}
              />
              {!isCreditNote && (
                <Box mt="sm">
                  <InvoiceRecoveryPlan />
                </Box>
              )}
              <Space h="xs" />
              <Comments
                submitComment={createInvoiceComment}
                resourceId={data.id}
                comments={comments}
                isCreateCommentPending={isCreateInvoiceCommentLoading}
              />
              {data.product_lines_attributes.length > 0 && (
                <Box mt="xs">
                  <ProductsDetail products={data.product_lines_attributes} />
                </Box>
              )}
            </div>
          )}
        </div>
      </InvoiceDetailContext.Provider>
      <Modal
        opened={isCustomeVariablesModalOpened}
        onClose={closeCustomVariablesModal}
        title={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE)}
      >
        <CustomVariablesForm onSubmit={editInvoice} initialValues={data} />
      </Modal>
    </>
  );
}
